import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const STATUS_META = {
  confirmed: { label: "Confirmed this login", cls: "bg-emerald-100 text-emerald-800" },
  pending: { label: "Not confirmed since last login", cls: "bg-amber-100 text-amber-800" },
  never: { label: "Never confirmed", cls: "bg-rose-100 text-rose-700" },
  no_login: { label: "Never logged in", cls: "bg-slate-100 text-slate-600" },
};

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

/** Admin dialog: who has / hasn't confirmed "Believe Before We Sell" on their latest login. */
export const ManifestoComplianceDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/gates/manifesto/compliance")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open]);

  const s = data?.summary || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="manifesto-compliance-dialog">
        <DialogHeader>
          <DialogTitle>&ldquo;Believe Before We Sell&rdquo; — compliance</DialogTitle>
          <DialogDescription>
            The gate re-opens on every login, so each employee should confirm again after each sign-in. Dealers are excluded.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#E53935]" size={26} /></div>
        ) : !data ? (
          <div className="p-6 text-sm text-rose-700">Couldn&rsquo;t load compliance data.</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" data-testid="compliance-summary">
              <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium">{s.confirmed || 0} confirmed this login</span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-medium">{s.pending || 0} pending re-confirm</span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 font-medium">{s.never || 0} never confirmed</span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 font-medium">{s.no_login || 0} never logged in</span>
            </div>
            <div className="overflow-y-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2 font-medium">Learner</th>
                    <th className="px-3 py-2 font-medium">Last login</th>
                    <th className="px-3 py-2 font-medium">Last confirmed</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.rows || []).map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.no_login;
                    return (
                      <tr key={r.learner_id || r.email} className="border-t border-slate-100" data-testid={`compliance-row-${r.learner_id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800">{r.name || "—"}</div>
                          <div className="text-xs text-slate-400">{r.email}</div>
                        </td>
                        <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmt(r.last_login_at)}</td>
                        <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmt(r.last_acknowledged_at)}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${meta.cls}`}>{meta.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManifestoComplianceDialog;
